import { ReactNode, RefObject } from "react";

import { DayHeaderContentArg, EventClickArg, EventContentArg, EventInput } from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Box, Typography } from "@mui/material";

import { ScopedBackdrop } from "../backdrop/scoped-backdrop/ScopedBackdrop";
import "./Calendar.css";
import { useCalendarAutoSize } from "./hooks/useCalendarAutoSize";

interface Props {
  calendarRef: RefObject<FullCalendar | null>;
  calendarEvents: EventInput[];
  backgroundEvents?: EventInput[];
  handleEventContent: (arg: EventContentArg) => ReactNode;
  handleEventClick: (clickInfo: EventClickArg) => void;
  noEventsMessage: string;
  onDateChange: (startDate: Date, endDate: Date) => void;
  isLoading: boolean;
  initialView: "listDay" | "timeGridWeek";
  dayHeaderContent?: (arg: DayHeaderContentArg) => ReactNode;
}

export function CalendarBase(props: Props) {
  const {
    calendarRef,
    calendarEvents,
    backgroundEvents = [],
    handleEventContent,
    handleEventClick,
    noEventsMessage,
    onDateChange,
    isLoading,
    initialView,
    dayHeaderContent,
  } = props;

  const containerRef = useCalendarAutoSize(calendarRef);

  return (
    <ScopedBackdrop open={isLoading} size={40} fill>
      <Box ref={containerRef} height="100%">
        <FullCalendar
          ref={calendarRef}
          locale={deLocale}
          plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin]}
          initialView={initialView}
          events={[...calendarEvents, ...backgroundEvents]}
          eventContent={handleEventContent}
          eventClick={handleEventClick}
          // Otherwise FullCalendar makes time-grid events focusable `<a>` elements whose Enter/Space
          // handler cancels the key for any link nested inside (the client name) and opens the event
          // instead. Mouse clicks are unaffected; keyboard users open an event via its RowActionButton.
          eventInteractive={false}
          noEventsContent={<Typography variant="body2">{noEventsMessage}</Typography>}
          datesSet={(info) => onDateChange(info.start, info.end)}
          headerToolbar={false}
          // FullCalendar reserves the all-day row even when it is empty; only show it when it has events.
          allDaySlot={calendarEvents.some((event) => event.allDay)}
          weekends={initialView !== "timeGridWeek"}
          firstDay={1}
          dayHeaderContent={dayHeaderContent}
          height="100%"
          eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
        />
      </Box>
    </ScopedBackdrop>
  );
}
